import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import {
  ArrowLeft,
  CheckCircle2,
  RefreshCw,
  Shield,
  Sparkles,
} from "lucide-react"

import { Button } from "@/components/button"
import { PageIntroSplitHeader } from "@/components/layout/page-intro-split-header"
import { EmptyState } from "@/components/shared/empty-state"
import { MetricTile } from "@/components/shared/metric-tile"
import { RefreshButton } from "@/components/shared/refresh-button"
import { SectionKicker } from "@/components/shared/section-kicker"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { DetailPageSkeleton } from "@/components/ui/page-skeletons"
import { formatGuildDate } from "@/lib/guild-date-format"
import { talentTrackingKeys } from "@/lib/query-keys"
import {
  talentTrackingApi,
  type TalentProgressBoss,
  type TalentProgressSeason,
  type TalentProgressTalent,
  type TalentTrackingCategory,
} from "@/lib/talent-tracking-api"

export const Route = createFileRoute("/_authed/_admin/talent-members/$userId")({
  component: TalentMemberRoute,
})

const CATEGORY_LABELS: Record<TalentTrackingCategory, string> = {
  offensive: "Offensive",
  defensive: "Defensive",
  strategic: "Strategic",
}

function TalentMemberRoute() {
  const { userId } = Route.useParams()
  const progressQuery = useQuery({
    queryKey: talentTrackingKeys.member(userId),
    queryFn: () => talentTrackingApi.getMemberProgress(userId),
  })

  if (progressQuery.isLoading) {
    return <DetailPageSkeleton sections={4} />
  }

  if (progressQuery.isError || !progressQuery.data) {
    return (
      <div className="grid gap-4">
        <StatusAlert tone="danger">
          {progressQuery.error instanceof Error
            ? progressQuery.error.message
            : "Failed to load member talent progress"}
        </StatusAlert>
        <Button
          type="button"
          variant="outline"
          onClick={() => void progressQuery.refetch()}
        >
          <RefreshCw className="size-4" />
          Retry
        </Button>
        <Link to="/talent-overview">
          <Button type="button" variant="outline">
            <ArrowLeft className="size-4" />
            Back to Overview
          </Button>
        </Link>
      </div>
    )
  }

  const progress = progressQuery.data
  const memberLabel = progress.displayName ?? progress.userId

  if (progress.seasons.length === 0) {
    return (
      <div className="grid gap-4">
        <EmptyState
          icon={<Shield className="size-5 text-muted-foreground" />}
          title="No talent progress found"
          description={`No seasonal talent data is available for ${memberLabel}. This member may not have any configured talents yet.`}
          actions={
            <Link to="/talent-overview">
              <Button type="button" variant="outline">
                <ArrowLeft className="size-4" />
                Back to Overview
              </Button>
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="grid gap-4">
      <Card className="page-intro border-primary/10">
        <PageIntroSplitHeader
          kicker={
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/talent-overview">
                <Button type="button" variant="outline">
                  <ArrowLeft className="size-4" />
                  Back to Overview
                </Button>
              </Link>
              <SectionKicker icon={<Sparkles />}>
                Member Drilldown
              </SectionKicker>
            </div>
          }
          title={`Talent readiness for ${memberLabel}`}
          titleClassName="max-w-4xl text-5xl leading-none font-semibold md:text-6xl truncate"
          description="Review the member's completed and missing boss talents across every configured season."
          descriptionClassName="max-w-3xl text-base leading-7"
          actions={
            <RefreshButton
              isFetching={progressQuery.isFetching}
              onClick={() => void progressQuery.refetch()}
            />
          }
          metrics={
            <>
              <MetricTile
                label="Completed"
                value={String(progress.completedTalentIds.length)}
                detail="Talents already marked complete."
              />
              <MetricTile
                label="Missing"
                value={String(progress.missingTalentIds.length)}
                detail="Talents still needed for full coverage."
              />
              <MetricTile
                label="Completion"
                value={`${Math.round(progress.completionRate * 100)}%`}
                detail="Overall completion across configured talents."
              />
            </>
          }
        />
      </Card>

      <div className="grid gap-4">
        {progress.seasons.map((season) => (
          <SeasonProgressCard key={season.season} season={season} />
        ))}
      </div>
    </div>
  )
}

function SeasonProgressCard({ season }: { season: TalentProgressSeason }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="size-5 text-primary" />
          <span className="truncate">{season.season}</span>
        </CardTitle>
        <CardDescription>
          {season.completedCount}/{season.totalCount} talents completed this
          season.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {season.bosses.length > 0 ? (
          season.bosses.map((boss) => (
            <BossProgressCard key={boss.id} boss={boss} />
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
            No bosses configured for this season.
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function BossProgressCard({ boss }: { boss: TalentProgressBoss }) {
  return (
    <div className="grid gap-4 rounded-3xl border border-border/60 bg-card/38 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="truncate text-lg font-semibold text-foreground">
            {boss.name}
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {boss.completedCount}/{boss.talents.length} completed
          </div>
        </div>
        <div className="rounded-full border border-border/70 px-3 py-1 text-xs font-semibold text-muted-foreground">
          {boss.missingCount} remaining
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {(
          ["offensive", "defensive", "strategic"] as TalentTrackingCategory[]
        ).map((category) => {
          const categoryTalents = boss.talentsByCategory[category]
          return (
            <div
              key={category}
              className="grid gap-3 rounded-[1.2rem] border border-border/50 bg-background/50 p-3"
            >
              <div className="text-kicker text-xs text-muted-foreground">
                {CATEGORY_LABELS[category]}
              </div>
              {categoryTalents.length > 0 ? (
                categoryTalents.map((talent) => (
                  <TalentStatusCard key={talent.id} talent={talent} />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-border/70 p-3 text-xs text-muted-foreground">
                  No {CATEGORY_LABELS[category].toLowerCase()} talents
                  configured.
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TalentStatusCard({ talent }: { talent: TalentProgressTalent }) {
  return (
    <div
      className={`inline-card grid gap-2 ${talent.completed ? "border-success/35 bg-success/10" : "bg-card/65"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-kicker text-xs text-muted-foreground">
          Tier {talent.tier}
        </div>
        {talent.completed ? (
          <CheckCircle2 className="size-4 text-success" />
        ) : null}
      </div>
      <div className="line-clamp-3 text-sm/6 font-medium text-foreground">
        {talent.description}
      </div>
      <div className="line-clamp-2 text-xs/5 text-muted-foreground">
        {talent.reward}
      </div>
      <div className="text-xs font-medium text-muted-foreground">
        {talent.completed
          ? "Completed"
          : talent.missingSince
            ? `Missing since ${formatGuildDate(talent.missingSince)}`
            : "Not completed yet"}
      </div>
    </div>
  )
}
